import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import YardIcon from "@mui/icons-material/Yard";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import HistoryIcon from "@mui/icons-material/History";
import api from "../../services/api";
import { IconButton } from "@mui/material";
import CoachHistoryDialog from "../History/CoachHistory/CoachHistoryDialog";
const today = () => new Date().toISOString().slice(0, 10);
const initial = {
  coach_no: "",
  gate_in_date: today(),
  gate_in_time: new Date().toTimeString().slice(0, 5),
  entry_type: "REPAIR",
  reason: "",
  received_from: "",
  yard_code: "YARD",
  remark: "",
};
const initialNew = {
  coach_type_code: "",
  railway_code: "",
  base_code: "",
  make_code: "",
  year_built: "",
  last_poh_date: "",
  last_out_date: "",
  repair_type_code: "",
  coach_remark: "",
};
const dateFmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "-");
export default function YardEntry() {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const limit = mobile ? 5 : 10;
  const [rows, setRows] = useState([]),
    [page, setPage] = useState(1),
    [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1),
    [search, setSearch] = useState(""),
    [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("ACTIVE");
  const [statusCounts, setStatusCounts] = useState({
    YARD: 0,
    ACTIVE: 0,
    COMPLETED: 0,
  });
  const [open, setOpen] = useState(false),
    [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initial),
    [newCoach, setNewCoach] = useState(initialNew);
  const [exists, setExists] = useState(false),
    [coach, setCoach] = useState(null),
    [openVisit, setOpenVisit] = useState(null);
  const [history, setHistory] = useState([]),
    [showNew, setShowNew] = useState(false),
    [checkLoading, setCheckLoading] = useState(false);
  const [masters, setMasters] = useState({
      coachTypes: [],
      railways: [],
      makes: [],
      repairTypes: [],
    }),
    [bases, setBases] = useState([]);
  const [msg, setMsg] = useState({ open: false, type: "success", text: "" });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCoachNo, setHistoryCoachNo] = useState("");
  const openCoachHistory = (coachNo) => {
    setHistoryCoachNo(coachNo);
    setHistoryOpen(true);
  };
  const notify = (text, type = "success") => setMsg({ open: true, type, text });
  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/yard/yard-entry", {
        params: { page, limit, search: search.trim(), status: tab },
      });
      setRows(r.data?.data || []);
      setTotal(Number(r.data?.pagination?.total || 0));
      setPages(Math.max(Number(r.data?.pagination?.totalPages || 1), 1));
      if (r.data?.statusCounts) {
        setStatusCounts({
          YARD: Number(r.data.statusCounts.YARD || 0),
          ACTIVE: Number(r.data.statusCounts.ACTIVE || 0),
          COMPLETED: Number(r.data.statusCounts.COMPLETED || 0),
        });
      }
    } catch (e) {
      notify(
        e.response?.data?.message || "Failed to load yard entries",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, tab]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    api
      .get("/coaches/masters")
      .then((r) => setMasters(r.data?.data || {}))
      .catch((e) =>
        notify(e.response?.data?.message || "Failed to load masters", "error"),
      );
  }, []);
  const change = (field, setter) => (e) =>
    setter((p) => ({ ...p, [field]: e.target.value }));
  const check = async () => {
    const no = form.coach_no.trim().toUpperCase();
    if (!no) return notify("Enter coach number first", "error");
    try {
      setCheckLoading(true);
      const r = await api.get(
        `/yard/yard-entry/check/${encodeURIComponent(no)}`,
      );
      setExists(!!r.data?.exists);
      setCoach(r.data?.coach || null);
      setOpenVisit(r.data?.openVisit || null);
      setHistory(r.data?.history || []);
      setShowNew(!r.data?.exists);
      notify(
        r.data?.exists
          ? "Existing coach found."
          : "Coach not found. Enter new coach details.",
        r.data?.exists ? "success" : "warning",
      );
    } catch (e) {
      notify(e.response?.data?.message || "Coach check failed", "error");
    } finally {
      setCheckLoading(false);
    }
  };
  const railwayChange = async (e) => {
    const code = e.target.value;
    setNewCoach((p) => ({ ...p, railway_code: code, base_code: "" }));
    setBases([]);
    const rway = masters.railways.find((x) => x.code === code);
    if (!rway?.id) return;
    try {
      const r = await api.get(`/coaches/bases-by-railway/${rway.id}`);
      setBases(r.data?.data || []);
    } catch (e) {
      notify(e.response?.data?.message || "Failed to load bases", "error");
    }
  };
  const submit = async () => {
    const no = form.coach_no.trim().toUpperCase();
    if (!no || !form.gate_in_date)
      return notify("Coach number and date are required", "error");
    try {
      setSaving(true);
      const c = await api.get(
        `/yard/yard-entry/check/${encodeURIComponent(no)}`,
      );
      if (c.data?.exists && c.data?.hasOpenVisit)
        return notify(
          `Coach already has open visit ${c.data.openVisit?.visit_no || ""}.`,
          "error",
        );
      const payload = {
        coach_id: c.data?.coach?.id || null,
        coach_no: no,
        ...form,
      };
      if (!c.data?.exists) {
        if (!showNew) return notify("Enter new coach details", "error");
        if (
          !newCoach.coach_type_code ||
          !newCoach.railway_code ||
          !newCoach.base_code
        )
          return notify("Coach Code, Railway and Base are required", "error");
        payload.new_coach = newCoach;
      }
      const r = await api.post("/yard/yard-entry", payload);
      notify(r.data?.message || "Yard entry created");
      setOpen(false);
      setForm({ ...initial, gate_in_date: today() });
      setNewCoach(initialNew);
      setShowNew(false);
      load();
    } catch (e) {
      notify(e.response?.data?.message || "Failed to save yard entry", "error");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, width: "100%", minWidth: 0 }}>
      <Box
        sx={{
          mb: 2.5,
          display: "flex",
          alignItems: {
            xs: "flex-start",
            sm: "center",
          },
          flexDirection: {
            xs: "column",
            sm: "row",
          },
          gap: 2,
        }}
      >
        <Stack direction="row" spacing={1.2} alignItems="center">
          <YardIcon color="warning" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Yard Entry
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Receive coach into workshop custody.
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setOpen(true);
            setForm({
              ...initial,
              gate_in_date: today(),
            });
            setCoach(null);
            setHistory([]);
            setOpenVisit(null);
            setExists(false);
            setShowNew(false);
          }}
          sx={{
            minWidth: 160,
            ml: {
              xs: 0,
              sm: "auto",
            },
          }}
        >
          New Yard Entry
        </Button>
      </Box>
      {open && (
        <Card
          sx={{
            mt: 2,
            border: "2px solid",
            borderColor: "warning.main",
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h6" fontWeight={800}>
                  New Yard Entry
                </Typography>
                <Chip label="YARD" color="warning" />
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr auto" },
                  gap: 1,
                }}
              >
                <TextField
                  label="Coach Number"
                  value={form.coach_no}
                  onChange={change("coach_no", setForm)}
                  onKeyDown={(e) => e.key === "Enter" && check()}
                  required
                />
                <Button
                  variant="outlined"
                  onClick={check}
                  disabled={checkLoading || !form.coach_no.trim()}
                  startIcon={
                    checkLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <SearchIcon />
                    )
                  }
                >
                  Check Coach
                </Button>
              </Box>
              {exists && coach && (
                <Paper
                  variant="outlined"
                  sx={{ p: 1.7, bgcolor: "success.50" }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <CheckCircleIcon color="success" />
                    <Typography fontWeight={800}>
                      Existing Coach Found
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr 1fr",
                        md: "repeat(4,1fr)",
                      },
                      gap: 1.5,
                    }}
                  >
                    {[
                      ["Coach No.", coach.coach_no],
                      [
                        "Coach Code",
                        coach.coach_type_code || coach.coach_type_name,
                      ],
                      ["Railway", coach.railway_code || coach.railway_name],
                      ["Division", coach.division_code || coach.division_name],
                      ["Base", coach.base_code || coach.base_name],
                      ["Make", coach.make_code || coach.make_name],
                      [
                        "Repair Type",
                        coach.repair_type_code || coach.repair_type_name,
                      ],
                      ["Last POH", dateFmt(coach.last_poh_date)],
                    ].map(([a, b]) => (
                      <Box key={a}>
                        <Typography variant="caption" color="text.secondary">
                          {a}
                        </Typography>
                        <Typography fontWeight={700}>{b || "-"}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
              {openVisit && (
                <Alert severity="error">
                  Coach already has open visit <b>{openVisit.visit_no}</b> at{" "}
                  <b>{openVisit.current_location_code}</b>. Gate-out/complete
                  that visit first.
                </Alert>
              )}
              {!exists && showNew && (
                <Paper
                  variant="outlined"
                  sx={{ p: 1.7, borderColor: "primary.main" }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                    <PersonAddAlt1Icon color="primary" />
                    <Typography fontWeight={800}>
                      New Coach Registration
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2,1fr)",
                        lg: "repeat(3,1fr)",
                      },
                      gap: 1.5,
                    }}
                  >
                    <SelectField
                      label="Coach Code"
                      value={newCoach.coach_type_code}
                      onChange={change("coach_type_code", setNewCoach)}
                      items={masters.coachTypes}
                    />
                    <SelectField
                      label="Railway"
                      value={newCoach.railway_code}
                      onChange={railwayChange}
                      items={masters.railways}
                    />
                    <SelectField
                      label="Base"
                      value={newCoach.base_code}
                      onChange={change("base_code", setNewCoach)}
                      items={bases}
                      valueKey="base_code"
                      labelKey="base_name"
                      disabled={!newCoach.railway_code}
                    />
                    <SelectField
                      label="Make"
                      value={newCoach.make_code}
                      onChange={change("make_code", setNewCoach)}
                      items={masters.makes}
                    />
                    <TextField
                      label="Year Built"
                      type="number"
                      value={newCoach.year_built}
                      onChange={change("year_built", setNewCoach)}
                    />
                    <SelectField
                      label="Repair Type"
                      value={newCoach.repair_type_code}
                      onChange={change("repair_type_code", setNewCoach)}
                      items={masters.repairTypes}
                    />
                  </Box>
                </Paper>
              )}
              <Divider />
              <Typography fontWeight={800}>Yard Entry Details</Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2,1fr)",
                    lg: "repeat(3,1fr)",
                  },
                  gap: 1.5,
                }}
              >
                <TextField
                  label="Gate-In Date"
                  type="date"
                  value={form.gate_in_date}
                  onChange={change("gate_in_date", setForm)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Gate-In Time"
                  type="time"
                  value={form.gate_in_time}
                  onChange={change("gate_in_time", setForm)}
                  InputLabelProps={{ shrink: true }}
                />
                <SelectField
                  label="Entry Type"
                  value={form.entry_type}
                  onChange={change("entry_type", setForm)}
                  items={[
                    { code: "POH", name: "POH" },
                    { code: "REPAIR", name: "Repair" },
                    { code: "SPECIAL_REPAIR", name: "Special Repair" },
                    { code: "OTHER", name: "Other" },
                  ]}
                />
                <TextField
                  label="Yard"
                  value={form.yard_code}
                  onChange={change("yard_code", setForm)}
                  required
                  disabled
                />
                <TextField
                  label="Received From"
                  value={form.received_from}
                  onChange={change("received_from", setForm)}
                />
                {/* <TextField
                  label="Reason"
                  value={form.reason}
                  onChange={change("reason", setForm)}
                /> */}
                <TextField
                  label="Remark"
                  value={form.remark}
                  onChange={change("remark", setForm)}
                  multiline
                  //minRows={2}
                />
              </Box>
              {history.length > 0 && (
                <Box>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <HistoryIcon color="primary" />
                    <Typography fontWeight={800}>
                      Previous Workshop Visits
                    </Typography>
                    <Chip
                      label={history.length}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Stack>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Visit</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Repair Type</TableCell>
                          <TableCell>Location</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {history.map((x) => (
                          <TableRow key={x.id}>
                            <TableCell>{x.visit_no}</TableCell>
                            <TableCell>{dateFmt(x.gate_in_date)}</TableCell>
                            <TableCell>{x.entry_type}</TableCell>
                            <TableCell>{x.current_location_code}</TableCell>
                            <TableCell>{x.status}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              <Stack
                direction={{ xs: "column-reverse", sm: "row" }}
                justifyContent="flex-end"
                spacing={1}
              >
                <Button
                  variant="outlined"
                  onClick={() => setOpen(false)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  onClick={submit}
                  disabled={saving || !!openVisit}
                >
                  {saving ? "Saving..." : "Save Yard Entry"}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
      <Card
        elevation={0}
        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}
      >
        <CardContent>
          <Tabs
            value={tab}
            onChange={(_, value) => {
              setTab(value);
              setPage(1);
            }}
            variant={mobile ? "fullWidth" : "standard"}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
          >
            <Tab value="YARD" label={`YARD (${statusCounts.YARD})`} />
            <Tab
              value="ACTIVE"
              label={`READY to OUT (${statusCounts.ACTIVE})`}
            />
            <Tab
              value="COMPLETED"
              label={`COMPLETED (${statusCounts.COMPLETED})`}
            />
          </Tabs>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search coach no., visit no., status..."
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
            <Chip
              label={`${total} Records`}
              color="warning"
              variant="outlined"
            />
          </Box>
          {loading ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : mobile ? (
            <Stack spacing={1}>
              {rows.map((x) => (
                <Paper key={x.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography fontWeight={800}>{x.coach_no}</Typography>
                      <Typography variant="caption">{x.visit_no}</Typography>
                    </Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Chip
                        size="small"
                        label={x.status}
                        color={x.status === "YARD" ? "warning" : "primary"}
                      />
                      <IconButton
                        size="small"
                        color="primary"
                        title="Coach History"
                        aria-label={`View history for ${x.coach_no}`}
                        onClick={() => openCoachHistory(x.coach_no)}
                      >
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    {dateFmt(x.gate_in_date)}{" "}
                    {String(x.gate_in_time || "").slice(0, 5)} •{" "}
                    {x.current_location_code}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {x.coach_type_code || x.coach_type_name || "-"} •{" "}
                    {x.base_code || x.base_name || "-"}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: "action.hover",
                      "& th": { fontWeight: 800 },
                    }}
                  >
                    {[
                      "Visit No.",
                      "Coach No.",
                      "Date",
                      "Repair Type",
                      "Yard",
                      "Status",
                      "Coach Code",
                      "Base",
                      "History",
                    ].map((h) => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((x) => (
                    <TableRow key={x.id} hover>
                      <TableCell>{x.visit_no}</TableCell>
                      <TableCell>
                        <b>{x.coach_no}</b>
                      </TableCell>
                      <TableCell>{dateFmt(x.gate_in_date)}</TableCell>
                      <TableCell>{x.entry_type}</TableCell>
                      <TableCell>{x.current_location_code}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={x.status}
                          color={x.status === "YARD" ? "warning" : "primary"}
                        />
                      </TableCell>
                      <TableCell>
                        {x.coach_type_code || x.coach_type_name || "-"}
                      </TableCell>
                      <TableCell>{x.base_code || x.base_name || "-"}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          title="Coach History"
                          aria-label={`View history for ${x.coach_no}`}
                          onClick={() => openCoachHistory(x.coach_no)}
                        >
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {pages > 1 && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={pages}
                page={page}
                onChange={(_, v) => setPage(v)}
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </CardContent>
      </Card>
      <CoachHistoryDialog
        coachNo={historyCoachNo}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
      <Snackbar
        open={msg.open}
        autoHideDuration={4000}
        onClose={() => setMsg((p) => ({ ...p, open: false }))}
      >
        <Alert severity={msg.type} variant="filled">
          {msg.text}
        </Alert>
      </Snackbar>
    </Box>
  );
}
function SelectField({
  label,
  value,
  onChange,
  items,
  valueKey = "code",
  labelKey = "name",
  disabled = false,
}) {
  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel>{label}</InputLabel>
      <Select value={value || ""} label={label} onChange={onChange}>
        <MenuItem value="">
          <em>Select</em>
        </MenuItem>
        {items.map((x) => (
          <MenuItem key={x[valueKey]} value={x[valueKey]}>
            {/* {x[labelKey]} ({x[valueKey]}) */}
            {x[labelKey] ? `${x[valueKey]} (${x[labelKey]})` : x[valueKey]}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
